import { useRef } from 'react';
import AdvancedSelect from '../../../../../Form/src/jsx/AdvancedSelect';
import { useBookingContext } from '../../contexts/BookingContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useTimeSlotGroups } from '../../hooks/useTimeSlotGroups';
import { TimeSlotEntry } from '../../types/booking';
import { formatDateRange } from '../../utils/dates';
import Loader from '../atoms/Loader';
import TimeSlot from '../molecules/TimeSlot';
import TimeSlotGroup from '../molecules/TimeSlotGroup';

interface TimeSelectionProps {
	getLabel: (key: string) => string;
	timeSlots: TimeSlotEntry[];
}

export default function TimeSelection({ getLabel, timeSlots }: TimeSelectionProps) {
	const { locale, appointment, timezone, availability, service } = useBookingContext();
	const { gtmEventSearchTimezone, gtmEventChangeTimezone } = useAnalytics(service.businessId, service.serviceId);
	const timeSlotGroups = useTimeSlotGroups(timeSlots, locale, timezone.current);
	const timeSlotGroupsRef = useRef<HTMLDivElement>(null);
	const { isAtTop, isAtBottom } = useScrollPosition(timeSlotGroupsRef, [timeSlotGroups]);

	const onTimezoneSearch = (searchTerm: string) => gtmEventSearchTimezone(searchTerm);
	const onTimezoneChange = (value: string) => {
		timezone.set(value);
		gtmEventChangeTimezone(value);
	};

	const getTimeSlotContent = () => {
		if (availability.isLoading) {
			return (
				<>
					<Loader />
					<p>{getLabel('time_slot_loading')}</p>
				</>
			);
		} else if (timeSlotGroups.length) {
			return (
				<div ref={timeSlotGroupsRef} className="time-slot-selection__groups">
					{timeSlotGroups.map(({ hour, label, suffix, slots }) => (
						<TimeSlotGroup key={hour} label={label} suffix={suffix}>
							{slots.map((slot: TimeSlotEntry, index) => (
								<TimeSlot
									key={slot.start_datetime}
									index={index}
									label={formatDateRange(new Date(slot.start_datetime), new Date(slot.end_datetime), locale, timezone.current)}
									onClick={() => appointment.setTimeSlot(slot)}
									selected={appointment.timeSlot?.start_datetime === slot.start_datetime}
									containerRef={timeSlotGroupsRef as React.RefObject<HTMLDivElement>}
								/>
							))}
						</TimeSlotGroup>
					))}
				</div>
			);
		} else {
			return <p>{getLabel('time_slot_none_available')}</p>;
		}
	};

	const timeSlotSelectionClasses = ['time-slot-selection'];
	if (availability.isLoading) timeSlotSelectionClasses.push('time-slot-selection--loading');
	if (timeSlotGroups.length === 0) timeSlotSelectionClasses.push('time-slot-selection--empty');

	return (
		<div className="booking__time-selection">
			<AdvancedSelect
				isSearchable={true}
				label={getLabel('form_field_time_zone')}
				name="timezone"
				onChange={onTimezoneChange}
				onSearch={onTimezoneSearch}
				options={timezone.options}
				value={timezone.current}
			/>
			<div className={timeSlotSelectionClasses.join(' ')}>
				<div className="time-slot-selection__wrapper">
					<div className="time-slot-selection__content">{getTimeSlotContent()}</div>
					<aside className="time-slot-selection__legend">
						<span className={`earlier${isAtTop ? ' hidden' : ''}`} aria-hidden={isAtTop}>
							{getLabel('time_slot_selection_scroll_up').toUpperCase()}
						</span>
						<span className={`later${isAtBottom ? ' hidden' : ''}`} aria-hidden={isAtBottom}>
							{getLabel('time_slot_selection_scroll_down').toUpperCase()}
						</span>
					</aside>
				</div>
			</div>
		</div>
	);
}
